import type { CapacityBarLegendItem } from "~/react/ui/capacity-bar"
import { CapacityBar, CapacityBarSegment } from "~/react/ui/capacity-bar"

type Props = {
  sent: number
  scheduled: number
  limit: number
  showLegend?: boolean
  chartSize?: "sm" | "md" | "lg"
  className?: string
}

export default function EmailCapacityBreakdown({
  sent,
  scheduled,
  limit,
  showLegend = false,
  chartSize = "md",
  className,
}: Props) {
  const effectiveLimit = Math.max(limit || 0, sent + scheduled, 1)
  const available = Math.max(effectiveLimit - sent - scheduled, 0)

  const pct = (value: number) =>
    Math.min(Math.max((value / effectiveLimit) * 100, 0), 100)

  const sentPercent = pct(sent)
  const scheduledPercent = pct(scheduled)
  const availablePercent = pct(available)

  const hasSecondary = scheduledPercent > 0
  const hasAvailable = availablePercent > 0
  const primaryOnly = sentPercent > 0 && !hasSecondary && !hasAvailable

  const legendItems: CapacityBarLegendItem[] = [
    {
      variant: "current",
      label: i18n.t("js.email_accounts_center.email_account.usage.legend.sent"),
      value: sent,
    },
    {
      variant: "secondary",
      label: i18n.t(
        "js.email_accounts_center.email_account.usage.legend.scheduled",
      ),
      value: scheduled,
    },
    {
      variant: "available",
      label: i18n.t(
        "js.email_accounts_center.email_account.usage.legend.available",
      ),
      value: available,
    },
  ]

  return (
    <CapacityBar
      size={chartSize}
      primaryOnly={primaryOnly}
      className={className}
      legendItems={showLegend ? legendItems : undefined}
    >
      {sentPercent > 0 && (
        <CapacityBarSegment variant="current" percent={sentPercent} />
      )}
      {hasSecondary && (
        <CapacityBarSegment variant="secondary" percent={scheduledPercent} />
      )}
      {hasAvailable && (
        <CapacityBarSegment variant="available" percent={availablePercent} />
      )}
    </CapacityBar>
  )
}
